import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { useLazyQuery } from "@apollo/client";

// Custom.
import Wrapper from "../Content/Wrapper";
import Report from "../Content/Report";
import Row from "../Content/Row";
import Header from "../Content/Header";
import { GET_STATISTICS_CARE_ASSISTANT_DIPLOMAS } from "api";
import { GeneralObject } from "@vocollege/app/dist/global";
import VoLoader from "@vocollege/components/dist/VoLoader";
import Stack from "@mui/material/Stack";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import { getStatisticsData, getStatisticsTotal } from "services/Utils";
import { DownloadExcelButtonProps } from "@vocollege/components/dist/DownloadExcelButton";
import { getSummaryEducatorType, getExcelData } from "../Utils";

const StatisticsCareAssistantDiplomas: React.FC = () => {
  const [parsedData, setParsedData] = useState<GeneralObject[]>([]);
  const [excelData, setExcelData] = useState<DownloadExcelButtonProps | null>(
    null
  );

  // Methods.

  const handleHeaderChange = (params: GeneralObject) => {
    loadQuery({
      variables: {
        from: params.from,
        to: params.to,
        groupBy: params.groupBy,
      },
    });
  };

  // Api.

  const [loadQuery, { data, loading, variables }] = useLazyQuery(
    GET_STATISTICS_CARE_ASSISTANT_DIPLOMAS,
    {
      fetchPolicy: "network-only",
      onCompleted: (loadedData) => {
        setParsedData(
          getStatisticsData(
            loadedData?.certStatisticsCareAssistantDiplomas.data
          )
        );
      },
    }
  );

  // Effects.

  useEffect(() => {
    setExcelData(
      getExcelData(
        data,
        parsedData,
        variables,
        "certStatisticsCareAssistantDiplomas",
        I18n.get.careAssistantDiploma.label
      )
    );
  }, [parsedData, data, variables]);

  return (
    <Wrapper
      title={I18n.get.careAssistantDiploma.labels.careAssistantDiplomas}
      hideTitleOnPrint
    >
      <Header
        filters={["from", "to", "groupBy"]}
        onChange={handleHeaderChange}
        excel={excelData || undefined}
      />
      {!loading && data && (
        <Box
          sx={{
            mt: 4,
            "@media print": {
              mt: 0,
            },
          }}
        >
          <Report
            title={I18n.get.careAssistantDiploma.label}
            subtitle={I18n.trans(I18n.get.vocertStatistics.perItem, {
              item: I18n.get.group.categories[variables?.groupBy].toLowerCase(),
            })}
            date={data?.certStatisticsCareAssistantDiplomas?.createdAt}
            interval={`${variables?.from} - ${variables?.to}`}
            summary={
              <Box>
                <Row
                  label={I18n.get.vocertStatistics.total}
                  value={getStatisticsTotal(parsedData)}
                />

                {getSummaryEducatorType(parsedData).map(
                  (v: GeneralObject, i: number) => (
                    <Row
                      key={i}
                      label={v.label}
                      note={v.note}
                      value={v.value}
                      size="small"
                    />
                  )
                )}
                <Divider variant="middle" sx={{ mt: 5 }} component="div">
                  <Typography component="h4" variant="h6" fontWeight={600}>
                    {I18n.trans(I18n.get.vocertStatistics.perItem, {
                      item: I18n.get.group.categories[
                        variables?.groupBy
                      ].toLowerCase(),
                    })}
                  </Typography>
                </Divider>
              </Box>
            }
          >
            <Stack>
              {parsedData.map((v: GeneralObject, i: number) => (
                <Box key={i}>
                  <Row key={i} label={v.title} value={v.total} />
                </Box>
              ))}
            </Stack>
          </Report>
        </Box>
      )}
      {loading && <VoLoader />}
    </Wrapper>
  );
};

export default StatisticsCareAssistantDiplomas;
